/**
 * Provenance audit — automated verification of entity source chains.
 *
 * Runs after every investigation completes. Checks:
 * 1. Does each source URL resolve (HTTP 200)?
 * 2. Does the extraction quote actually appear on the source page?
 *
 * Air-gap/offline note: When URLs are unreachable and no cached data
 * is available, the audit reports "unverifiable" rather than "failed" —
 * the entity is not penalized for network unavailability.
 */

import type { Entity } from '../entities/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProvenanceAuditEntry {
  entityId: string;
  entityName: string;
  sourceUrl: string;
  urlReachable: boolean | 'unverifiable';
  quoteFound: boolean | 'unverifiable';
  score: number; // 0-1
}

export interface ProvenanceAuditReport {
  results: ProvenanceAuditEntry[];
  overallScore: number;
  verifiedCount: number;
  unverifiableCount: number;
  totalEntities: number;
}

// ---------------------------------------------------------------------------
// Audit execution
// ---------------------------------------------------------------------------

const FETCH_TIMEOUT_MS = 10_000;

/**
 * Audit provenance chains for a set of entities.
 *
 * @param entities - Entities to audit
 * @param cachedData - Optional map of collectorId → raw HTML for air-gap fallback
 */
export async function auditProvenance(
  entities: Entity[],
  cachedData?: Map<string, string>,
): Promise<ProvenanceAuditReport> {
  const results: ProvenanceAuditEntry[] = [];

  for (const entity of entities) {
    if (!entity.provenance?.sourceUrl) {
      results.push({
        entityId: entity.id,
        entityName: entity.name,
        sourceUrl: '',
        urlReachable: 'unverifiable',
        quoteFound: 'unverifiable',
        score: 0,
      });
      continue;
    }

    let urlReachable: boolean | 'unverifiable' = 'unverifiable';
    let quoteFound: boolean | 'unverifiable' = 'unverifiable';
    let pageText = '';

    // Try cached data first (for air-gap/offline)
    const cached = cachedData?.get(entity.provenance.collectorId);
    if (cached) {
      pageText = cached;
      urlReachable = true; // Data was reachable at collection time
    } else {
      // Try live fetch
      try {
        const response = await fetch(entity.provenance.sourceUrl, {
          signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
          headers: { 'User-Agent': 'Sentinel-OSINT/1.0' },
        });
        urlReachable = response.ok;
        if (response.ok) {
          pageText = await response.text();
        }
      } catch {
        urlReachable = 'unverifiable';
      }
    }

    // Check extraction quote — normalize whitespace for fuzzy comparison
    if (pageText && entity.provenance.extractionQuote) {
      const normalizedPage = pageText.toLowerCase().replace(/\s+/g, ' ');
      const normalizedQuote = entity.provenance.extractionQuote.toLowerCase().replace(/\s+/g, ' ');
      quoteFound = normalizedPage.includes(normalizedQuote);
    }

    const score = (urlReachable === true ? 0.5 : 0) + (quoteFound === true ? 0.5 : 0);
    results.push({
      entityId: entity.id,
      entityName: entity.name,
      sourceUrl: entity.provenance.sourceUrl,
      urlReachable,
      quoteFound,
      score,
    });
  }

  const verifiable = results.filter(r => r.urlReachable !== 'unverifiable');
  const overallScore = verifiable.length > 0
    ? verifiable.reduce((sum, r) => sum + r.score, 0) / verifiable.length
    : 0;

  return {
    results,
    overallScore,
    verifiedCount: verifiable.length,
    unverifiableCount: results.length - verifiable.length,
    totalEntities: entities.length,
  };
}
